import React, { useState, useMemo } from 'react';
import { FaPaperclip, FaTrash } from 'react-icons/fa';
import { ChevronDown, ChevronUp, ChevronRight, AlertTriangle, Info, Package } from 'react-feather';
import { Button } from '../ui/button';
import { TextField } from '../ui/TextField';
import { TextArea } from '../ui/TextArea';
import InlineTagEditor from '../FeatureTags/InlineTagEditor';
import TaskTypeIcon from '../shared/TaskTypeIcon';
import TaskDueDate from '../shared/TaskDueDate';
import { FeatherArrowRight, FeatherCheckCircle } from '../ui/icons';
import { Tooltip } from '../ui/Tooltip';
import { isReleaseReadyForRelease } from '../../utils/releaseUtils';
import ReadyForReleaseBadge from '../Releases/ReadyForReleaseBadge';
import { useAuth } from '../../context/AuthContext';
import FeatureTagDetailModal from '../FeatureTags/FeatureTagDetailModal';
import { FeatureTag } from '../ui/feature-tag';
import TaskLinks from './TaskLinks';
import RelationshipBadge from './RelationshipBadge';
import './TaskList.css';
import { taskApi } from '../../services/api';
import TaskTypeSelect from '../shared/TaskTypeSelect';
import { DEFAULT_TASK_TYPE, TASK_TYPE_SUBTASK, isSubtaskType } from '../../constants/taskTypes';

const TaskList = ({ tasks, allTasks = [], subtaskIds = new Set(), releases = [], onStatusChange, onAssigneeChange, users, isManager, setTasks, viewMode = 'grid', availableTags, projectId, onTagUpdate, onTagCreate, onTaskExplore, onAddSubtask, groupingMode = 'none', groupedTasks = {}, groupedTasksByWorkPlan = {}, groupedTasksByReleases = {}, groupedTasksByLinks = {}, expandedGroups = new Set(), onToggleGroup, isFeatureOverdue, allowCrossProject = false, subtaskBubblesMode = false, onLoadMoreGroup = null, loadingMoreGroupId = null, hasMoreFlat = false, onLoadMoreFlat = null, loadingMoreFlat = false }) => {
    const { user } = useAuth();
    
    // Filter out users with role "unknown"
    const filteredUsers = useMemo(() => {
        return users ? users.filter(user => user.role !== 'unknown') : [];
    }, [users]);
    const statusOptions = ['open', 'in-progress', 'completed', 'released'];
    const [changedTasks, setChangedTasks] = useState({});
    const [selectedTask, setSelectedTask] = useState(null);
    const [showAttachments, setShowAttachments] = useState(false);
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');
    const [deleteConfirmation, setDeleteConfirmation] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [savingTaskId, setSavingTaskId] = useState(null);
    const [selectedFeatureTag, setSelectedFeatureTag] = useState(null);
    const [isFeatureTagModalOpen, setIsFeatureTagModalOpen] = useState(false);
    const [expandedCompactTasks, setExpandedCompactTasks] = useState(new Set());

    const toggleCompactExpand = (taskId, e) => {
        if (e) e.stopPropagation();
        setExpandedCompactTasks(prev => {
            const next = new Set(prev);
            if (next.has(taskId)) next.delete(taskId);
            else next.add(taskId);
            return next;
        });
    };

    const getStatusBadgeClass = (status) => {
        const baseClass = 'status-badge';
        switch (status.toLowerCase()) {
            case 'open':
                return `${baseClass} status-open`;
            case 'in-progress':
                return `${baseClass} status-in-progress`;
            case 'completed':
                return `${baseClass} status-completed`;
            case 'released':
                return `${baseClass} status-released`;
            default:
                return baseClass;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const calculateTaskMetrics = (task) => {
        const workPlanTasks = task.work_plan_tasks || [];
        const now = new Date();
        
        // Calculate dates
        const createdDate = new Date(task.created_at);
        const daysSinceCreated = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
        
        // Calculate work statistics
        const totalHours = workPlanTasks.reduce((sum, wpt) => sum + (parseFloat(wpt.hours) || 0), 0);
        const workCount = workPlanTasks.length;
        
        // Get first and last worked dates
        const workDates = workPlanTasks
            .map(wpt => new Date(wpt.created_at))
            .sort((a, b) => a - b);
        const firstWorkedDate = workDates.length > 0 ? workDates[0] : null;
        const lastWorkedDate = workDates.length > 0 ? workDates[workDates.length - 1] : null;
        
        // Calculate stale status (only if worked on at least once)
        const daysSinceLastWork = lastWorkedDate 
            ? Math.floor((now - lastWorkedDate) / (1000 * 60 * 60 * 24))
            : null;
        const isStale = lastWorkedDate && daysSinceLastWork > 7;
        
        return {
            totalHours: totalHours.toFixed(1),
            workCount,
            firstWorkedDate,
            lastWorkedDate,
            isOld: daysSinceCreated > 30,
            isStale
        };
    };

    const checkTaskAssociations = (task) => {
        const associations = {
            workPlans: [],
            featureTag: null,
            releases: [],
            taskAttachments: 0,
            workPlanAttachments: 0,
            hasNotes: false
        };

        // Check work plan associations
        const workPlanTasks = task.work_plan_tasks || [];
        if (workPlanTasks.length > 0) {
            associations.workPlans = workPlanTasks.map(wpt => ({
                id: wpt.id,
                hours: wpt.hours || 0,
                notes: wpt.notes || '',
                created_at: wpt.created_at
            }));
            
            // Count work plan attachments
            workPlanTasks.forEach(wpt => {
                if (wpt.work_plan_attachments && wpt.work_plan_attachments.length > 0) {
                    associations.workPlanAttachments += wpt.work_plan_attachments.length;
                }
            });

            // Check if any work plan tasks have notes
            associations.hasNotes = workPlanTasks.some(wpt => wpt.notes && wpt.notes.trim().length > 0);
        }

        // Check feature tag association
        if (task.feature_tag) {
            associations.featureTag = {
                id: task.feature_tag.id,
                name: task.feature_tag.name,
                color: task.feature_tag.color
            };
        }

        // Check release associations (if feature tag exists and releases are available)
        if (associations.featureTag && releases && releases.length > 0) {
            releases.forEach(release => {
                if (release.release_feature_tags && release.release_feature_tags.length > 0) {
                    const hasFeatureTag = release.release_feature_tags.some(
                        rft => rft.feature_tag?.id === associations.featureTag.id
                    );
                    if (hasFeatureTag) {
                        associations.releases.push({
                            id: release.id,
                            name: release.name,
                            version: release.version,
                            status: release.status,
                            target_date: release.target_date
                        });
                    }
                }
            });
        }

        // Check task attachments
        if (task.attachments && task.attachments.length > 0) {
            associations.taskAttachments = task.attachments.length;
        }

        return associations;
    };

    const handleStatusChange = (taskId, newStatus) => {
        setChangedTasks(prev => ({
            ...prev,
            [taskId]: {
                ...prev[taskId],
                status: newStatus,
                originalStatus: tasks.find(t => t.id === taskId).status
            }
        }));
    };

    const handleAssigneeChange = (taskId, newAssigneeId) => {
        setChangedTasks(prev => ({
            ...prev,
            [taskId]: {
                ...prev[taskId],
                assignedTo: newAssigneeId,
                originalAssignedTo: tasks.find(t => t.id === taskId).assigned_to?.id
            }
        }));
    };

    const handleFieldChange = (taskId, field, value) => {
        setChangedTasks(prev => ({
            ...prev,
            [taskId]: {
                ...prev[taskId],
                [field]: value,
                [`original${field.charAt(0).toUpperCase() + field.slice(1)}`]: tasks.find(t => t.id === taskId)[field]
            }
        }));
    };

    const handleSave = async (taskId) => {
        const changes = changedTasks[taskId];
        if (!changes || savingTaskId) return;

        setSavingTaskId(taskId);
        try {
        const task = tasks.find(t => t.id === taskId);
        const updatedTask = { ...task };
        let hasFieldChanges = false;

        // Update all changed fields
        if (changes.title !== undefined && changes.title !== changes.originalTitle) {
            updatedTask.title = changes.title;
            hasFieldChanges = true;
        }
        if (changes.description !== undefined && changes.description !== changes.originalDescription) {
            updatedTask.description = changes.description;
            hasFieldChanges = true;
        }
        if (changes.taskType !== undefined && changes.taskType !== changes.originalTaskType) {
            updatedTask.task_type = changes.taskType;
            hasFieldChanges = true;
        }
        if (changes.status !== undefined && changes.status !== changes.originalStatus) {
            updatedTask.status = changes.status;
            await onStatusChange(taskId, changes.status);
        }
        if (changes.assignedTo !== undefined && changes.assignedTo !== changes.originalAssignedTo) {
            updatedTask.assigned_to = { id: changes.assignedTo };
            await onAssigneeChange(taskId, changes.assignedTo);
        }

        // Update title/description/task_type if changed
        if (hasFieldChanges) {
            const updateData = {
                title: updatedTask.title,
                description: updatedTask.description
            };
            if (updatedTask.task_type) {
                updateData.task_type = updatedTask.task_type;
            }
            await taskApi.update(taskId, updateData);
            // Optimistically update the task in the local tasks array
            if (setTasks) {
                setTasks(prevTasks => prevTasks.map(t => t.id === taskId ? { ...t, title: updatedTask.title, description: updatedTask.description, task_type: updatedTask.task_type } : t));
            }
        }

        // Clear changes for this task after saving
        setChangedTasks(prev => {
            const newChanges = { ...prev };
            delete newChanges[taskId];
            return newChanges;
        });
        setEditingTaskId(null);
        } finally {
            setSavingTaskId(null);
        }
    };

    const handleCancel = (taskId) => {
        setChangedTasks(prev => {
            const newChanges = { ...prev };
            delete newChanges[taskId];
            return newChanges;
        });
        setEditingTaskId(null);
    };

    const hasChanges = (taskId) => {
        const changes = changedTasks[taskId];
        if (!changes) return false;

        const task = tasks.find(t => t.id === taskId);
        return (
            changes.title !== task.title ||
            changes.description !== task.description ||
            changes.status !== task.status ||
            changes.assignedTo !== task.assigned_to?.id ||
            (changes.taskType || DEFAULT_TASK_TYPE) !== (task.task_type || DEFAULT_TASK_TYPE)
        );
    };

    const handleAttachmentClick = (task) => {
        setSelectedTask(task);
        setShowAttachments(true);
    };

    const handleCloseAttachments = () => {
        setShowAttachments(false);
        setSelectedTask(null);
    };

    const startEditing = (taskId) => {
        const task = tasks.find(t => t.id === taskId);
        setChangedTasks(prev => ({
            ...prev,
            [taskId]: {
                title: task.title,
                description: task.description,
                status: task.status,
                assignedTo: task.assigned_to?.id,
                taskType: task.task_type || DEFAULT_TASK_TYPE,
                originalTitle: task.title,
                originalDescription: task.description,
                originalStatus: task.status,
                originalAssignedTo: task.assigned_to?.id,
                originalTaskType: task.task_type || DEFAULT_TASK_TYPE
            }
        }));
        setEditingTaskId(taskId);
    };

    const handleAttachmentUpload = async (taskId, event) => {
        const file = event.target.files[0];
        if (!file) return;
        setUploading(true);
        setUploadError('');
        try {
            // Assume taskApi.uploadAttachment returns the new attachment object
            const newAttachment = await taskApi.uploadAttachment(taskId, file);
            // Update the selectedTask's attachments in state
            setSelectedTask(prev => ({
                ...prev,
                attachments: [...(prev.attachments || []), newAttachment]
            }));
            // Update the task in the main tasks array
            if (setTasks) {
                setTasks(prevTasks => prevTasks.map(task => 
                    task.id === taskId 
                        ? { ...task, attachments: [...(task.attachments || []), newAttachment] }
                        : task
                ));
            }
        } catch (err) {
            setUploadError('Failed to upload attachment.');
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteAttachment = async (attachmentId) => {
        if (!selectedTask) return;
        try {
            await taskApi.deleteAttachment(selectedTask.id, attachmentId);
            // Update selectedTask state
            setSelectedTask(prev => ({
                ...prev,
                attachments: prev.attachments.filter(att => att.id !== attachmentId)
            }));
            // Update the task in the main tasks array
            if (setTasks) {
                setTasks(prevTasks => prevTasks.map(task => 
                    task.id === selectedTask.id 
                        ? { ...task, attachments: task.attachments.filter(att => att.id !== attachmentId) }
                        : task
                ));
            }
        } catch (err) {
            alert('Failed to delete attachment.');
        }
    };

    const handleCopyDisplayId = async (displayId) => {
        try {
            await navigator.clipboard.writeText(displayId);
            // You could add a toast notification here
            console.log('Display ID copied to clipboard:', displayId);
        } catch (error) {
            console.error('Failed to copy display ID:', error);
        }
    };

    const handleDeleteClick = (task) => {
        // Check associations first (this already checks work_plan_tasks)
        const associations = checkTaskAssociations(task);
        
        // Use workPlans.length from associations instead of recalculating
        if (associations.workPlans.length > 0) {
            alert(`Cannot delete task "${task.title}". This task has been worked on ${associations.workPlans.length} time(s) and cannot be deleted.`);
            return;
        }
        
        setDeleteConfirmation({
            task,
            associations
        });
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmation(null);
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation) return;
        
        const taskId = deleteConfirmation.task.id;
        try {
            setDeleting(true);
            await taskApi.delete(taskId);
            
            // Update tasks state
            if (setTasks) {
                setTasks(prevTasks => prevTasks.filter(t => t.id !== taskId));
            }
            
            setDeleteConfirmation(null);
        } catch (error) {
            console.error('Failed to delete task:', error);
            alert('Failed to delete task. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const renderTaskCard = (task) => {
        const metrics = calculateTaskMetrics(task);
        const userHasPrivileges = user?.email === 'nikhil@smartrabbitz.com' || user?.email === 'jyothish@smartrabbitz.com';
        const canDelete = userHasPrivileges && metrics.workCount === 0;
        
        // Get release information for this task
        const taskReleases = [];
        if (task.feature_tag && releases && releases.length > 0) {
            releases.forEach(release => {
                if (release.release_feature_tags && release.release_feature_tags.length > 0) {
                    const hasFeatureTag = release.release_feature_tags.some(
                        rft => rft.feature_tag?.id === task.feature_tag.id
                    );
                    if (hasFeatureTag) {
                        taskReleases.push({
                            id: release.id,
                            name: release.name,
                            version: release.version,
                            status: release.status,
                            target_date: release.target_date
                        });
                    }
                }
            });
        }
        
        const isSubtask = subtaskIds.has(task.id);
        return (
        <div key={task.id} className={`task-row${isSubtask ? ' task-row--subtask' : ''}`}>
        <div className="task-card">
                            <div className="task-header">
                                <div className="task-title-container">
                                    {editingTaskId === task.id ? (
                                        <TextField>
                                            <TextField.Input
                                                value={changedTasks[task.id]?.title || task.title}
                                                onChange={(e) => handleFieldChange(task.id, 'title', e.target.value)}
                                                placeholder="Task title"
                                                className="task-title-input"
                                            />
                                        </TextField>
                                    ) : (
                                        <div className="task-title-with-icon">
                                            <TaskTypeIcon taskType={task.task_type} />
                                            <h3 className="task-title">
                                                {task.display_id && (
                                                    <span 
                                                        className="task-display-id" 
                                                        onClick={() => handleCopyDisplayId(task.display_id)}
                                                        title="Click to copy"
                                                    >
                                                        [{task.display_id}]
                                                    </span>
                                                )}
                                                {task.title}
                                            </h3>
                                        </div>
                                    )}
                                    {task.attachments && task.attachments.length > 0 && (
                                        <button
                                            className="attachment-icon"
                                            onClick={() => handleAttachmentClick(task)}
                                            title={`${task.attachments.length} attachment(s)`}
                                        >
                                            <FaPaperclip />
                                            <span className="attachment-count">{task.attachments.length}</span>
                                        </button>
                                    )}
                                </div>
                                <div className="task-header-badges">
                                    <div className="task-marker-badges">
                                        {metrics.isOld && (
                                            <span className="task-marker-badge marker-old">
                                                30+ days old
                                            </span>
                                        )}
                                        {metrics.isStale && (
                                            <span className="task-marker-badge marker-stale">
                                                Stale
                                            </span>
                                        )}
                                    </div>
                                    {editingTaskId === task.id ? (
                                        <select 
                                            value={changedTasks[task.id]?.status || task.status}
                                            onChange={(e) => handleFieldChange(task.id, 'status', e.target.value)}
                                            className="status-select"
                                        >
                                            {statusOptions.map(status => (
                                                <option key={status} value={status}>
                                                    {status.charAt(0).toUpperCase() + status.slice(1)}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className={getStatusBadgeClass(task.status)}>
                                            {task.status}
                                        </span>
                                    )}
                                    {onTaskExplore && (
                                        <Tooltip content="View Task Summary">
                                            <button
                                                className="explore-task-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onTaskExplore(task);
                                                }}
                                                aria-label="View task summary"
                                            >
                                                <FeatherArrowRight size={16} />
                                            </button>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>

                            {editingTaskId === task.id ? (
                                <TextArea>
                                    <TextArea.Input
                                        value={changedTasks[task.id]?.description || task.description || ''}
                                        onChange={(e) => handleFieldChange(task.id, 'description', e.target.value)}
                                        placeholder="Task description"
                                        className="task-description-input"
                                    />
                                </TextArea>
                            ) : (
                                <p className="task-description">
                                    {task.description || 'No description provided'}
                                </p>
                            )}

                            {/* Task Type Field */}
                            {editingTaskId === task.id ? (
                                <div className="task-meta">
                                    <div className="task-assignee">
                                        <span className="meta-label">Task Type:</span>
                                        <TaskTypeSelect
                                            value={changedTasks[task.id]?.taskType || task.task_type || DEFAULT_TASK_TYPE}
                                            onChange={(e) => handleFieldChange(task.id, 'taskType', e.target.value)}
                                            className="assignee-select"
                                            excludeTypes={isSubtaskType(task.task_type) ? [] : [TASK_TYPE_SUBTASK]}
                                            disabled={isSubtaskType(task.task_type)}
                                        />
                                    </div>
                                </div>
                            ) : null}

                            {/* Feature Tag Editor */}
                            <div className="task-tags">
                                <div className="task-tags-main">
                                    <InlineTagEditor
                                        task={task}
                                        availableTags={availableTags || []}
                                        projectId={projectId}
                                        onTagUpdate={onTagUpdate}
                                        onTagCreate={onTagCreate}
                                    />
                                    {task.feature_tag && (
                                        <Tooltip content="View feature details">
                                            <button
                                                className="feature-tag-view-button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const fullFeatureTag = availableTags.find(ft => ft.id === task.feature_tag.id) || task.feature_tag;
                                                    setSelectedFeatureTag(fullFeatureTag);
                                                    setIsFeatureTagModalOpen(true);
                                                }}
                                                aria-label="View feature details"
                                            >
                                                <Info size={14} />
                                            </button>
                                        </Tooltip>
                                    )}
                                </div>
                                <TaskDueDate dueDate={task.feature_tag?.due_date} taskStatus={task.status} />
                                {taskReleases.length > 0 && (
                                    <div className="task-release-info">
                                        {taskReleases.map(release => (
                                            <span 
                                                key={release.id} 
                                                className="task-release-badge" 
                                                title={`Release: ${release.name}${release.version ? ` (v${release.version})` : ''}${release.target_date ? ` - ${formatDate(release.target_date)}` : ''}`}
                                            >
                                                <Package size={11} />
                                                <span className="task-release-name">{release.name}</span>
                                                {release.version && <span className="task-release-version">v{release.version}</span>}
                                                {release.target_date && (
                                                    <span className="task-release-date-separator">•</span>
                                                )}
                                                {release.target_date && (
                                                    <span className="task-release-date">{formatDate(release.target_date)}</span>
                                                )}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div className="task-meta">
                                <div className="task-assignee">
                                    <span className="meta-label">Assigned to:</span>
                                    {editingTaskId === task.id ? (
                                        <select
                                            value={changedTasks[task.id]?.assignedTo || task.assigned_to?.id || ''}
                                            onChange={(e) => handleFieldChange(task.id, 'assignedTo', e.target.value)}
                                            className="assignee-select"
                                        >
                                            <option value="">Unassigned</option>
                                            {filteredUsers.map(user => (
                                                <option key={user.id} value={user.id}>
                                                    {user.display_name || user.email}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span>{task.assigned_to?.display_name || 'Unassigned'}</span>
                                    )}
                                </div>

                                <div className="task-dates">
                                    <span className="meta-label">Created:</span>
                                    <span>{formatDate(task.created_at)}</span>
                                </div>

                                <div className="task-creator">
                                    <span className="meta-label">Created by:</span>
                                    <span>{task.created_by?.display_name || 'Unknown'}</span>
                                </div>
                            </div>

                            <div className="task-metrics">
                                <div className="task-metric-item">
                                    <span className="meta-label">Total hours:</span>
                                    <span>{metrics.totalHours}h</span>
                                </div>
                                <div className="task-metric-item">
                                    <span className="meta-label">Times worked on:</span>
                                    <span>{metrics.workCount}</span>
                                </div>
                                {metrics.firstWorkedDate && (
                                    <div className="task-metric-item">
                                        <span className="meta-label">First worked:</span>
                                        <span>{formatDate(metrics.firstWorkedDate)}</span>
                                    </div>
                                )}
                                {metrics.lastWorkedDate && (
                                    <div className="task-metric-item">
                                        <span className="meta-label">Last worked:</span>
                                        <span>{formatDate(metrics.lastWorkedDate)}</span>
                                    </div>
                                )}
                            </div>

                            {/* Task Links Section - Placed before actions for better separation */}
                            <TaskLinks
                                task={task}
                                allTasks={allTasks}
                                projectId={projectId}
                                onTaskClick={(linkedTaskId) => {
                                    // Find and scroll to the linked task
                                    const linkedTask = allTasks.find(t => t.id === linkedTaskId);
                                    if (linkedTask && onTaskExplore) {
                                        onTaskExplore(linkedTask);
                                    }
                                }}
                                isManager={isManager}
                                allowCrossProject={allowCrossProject}
                                onAddSubtask={onAddSubtask}
                                onTaskUpdate={setTasks ? (updatedTask) => setTasks(prev => prev.map(t => t.id === updatedTask.id ? { ...t, ...updatedTask } : t)) : undefined}
                                onLinksUpdated={setTasks ? (taskId, links) => setTasks(prev => prev.map(t => t.id === taskId ? { ...t, linked_tasks: links } : t)) : undefined}
                            />

                            <div className="task-actions">
                                {editingTaskId === task.id ? (
                                    <>
                                        <Button 
                                            variant="primary"
                                            onClick={() => handleSave(task.id)}
                                            disabled={!hasChanges(task.id) || savingTaskId === task.id}
                                        >
                                            {savingTaskId === task.id ? 'Saving...' : 'Save Changes'}
                                        </Button>
                                        <Button 
                                            variant="neutral"
                                            onClick={() => handleCancel(task.id)}
                                        >
                                            Cancel
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button 
                                            variant="neutral"
                                            onClick={() => startEditing(task.id)}
                                        >
                                            Edit Task
                                        </Button>
                                        {userHasPrivileges && (
                                            <Button 
                                                variant="danger"
                                                onClick={() => handleDeleteClick(task)}
                                                disabled={!canDelete}
                                                title={!canDelete ? `Cannot delete: Task has been worked on ${metrics.workCount} time(s)` : 'Delete task'}
                                            >
                                                Delete
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
        </div>
        );
    };

    const renderCompactTaskCard = (task) => {
        const metrics = calculateTaskMetrics(task);
        const userHasPrivileges = user?.email === 'nikhil@smartrabbitz.com' || user?.email === 'jyothish@smartrabbitz.com';
        const canDelete = userHasPrivileges && metrics.workCount === 0;
        const isExpanded = expandedCompactTasks.has(task.id);
        const isSubtask = subtaskIds.has(task.id);

        const taskReleases = [];
        if (task.feature_tag && releases && releases.length > 0) {
            releases.forEach(release => {
                if (release.release_feature_tags?.some(rft => rft.feature_tag?.id === task.feature_tag.id)) {
                    taskReleases.push({
                        id: release.id,
                        name: release.name,
                        version: release.version,
                        target_date: release.target_date
                    });
                }
            });
        }

        const metadataParts = [
            task.assigned_to?.display_name || task.assigned_to?.email || 'Unassigned',
            metrics.lastWorkedDate ? formatDate(metrics.lastWorkedDate) : formatDate(task.created_at)
        ].filter(Boolean);
        const metadataLine = metadataParts.join(' • ');
        const hasBadges = true; // Always show badges section for feature tag + markers

        const handleRowKeyDown = (e) => {
            // Don't trigger collapse when editing
            if (editingTaskId === task.id) return;
            // Don't trigger collapse when user is typing in form controls
            const isFormControl = e.target.closest?.('input, textarea, select, [contenteditable="true"]');
            if (isFormControl) return;

            if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleCompactExpand(task.id, e);
            }
        };

        return (
            <div
                key={task.id}
                className={`task-row task-compact-row${isSubtask ? ' task-row--subtask' : ''}${isExpanded ? ' task-compact-row--expanded' : ''}`}
                role="button"
                tabIndex={0}
                onClick={() => !editingTaskId && toggleCompactExpand(task.id)}
                onKeyDown={handleRowKeyDown}
                aria-expanded={isExpanded}
                aria-label={`${task.title}, ${task.status}. ${isExpanded ? 'Expanded' : 'Collapsed'}. Press Enter to ${isExpanded ? 'collapse' : 'expand'}`}
            >
                {/* Collapsed: 2 lines */}
                <div className="task-compact-collapsed">
                    <div className="task-compact-line1" style={{ background: isSubtask ? 'white' : '#e4e4eb' }}>
                        <TaskTypeIcon taskType={task.task_type} size={14} className="task-compact-type-icon" />
                        <span className="task-compact-title" title={task.title}>
                            {task.display_id && <span className="task-compact-display-id">[{task.display_id}]</span>}
                            {task.title || 'Untitled'}
                        </span>
                        <span className={`task-compact-status ${getStatusBadgeClass(task.status)}`}>{task.status}</span>
                        <span className="task-compact-date">{formatDate(task.created_at)}</span>
                        {task.attachments?.length > 0 && (
                            <button
                                className="task-compact-attachment"
                                onClick={(e) => { e.stopPropagation(); handleAttachmentClick(task); }}
                                title={`${task.attachments.length} attachment(s)`}
                                aria-label={`${task.attachments.length} attachments`}
                            >
                                <FaPaperclip size={10} />
                            </button>
                        )}
                        <button
                            className={`task-compact-expand-btn${editingTaskId === task.id ? ' task-compact-expand-btn--disabled' : ''}`}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (editingTaskId === task.id) return; // Don't collapse while editing
                                toggleCompactExpand(task.id, e);
                            }}
                            aria-label={isExpanded ? 'Collapse' : 'Expand'}
                            aria-disabled={editingTaskId === task.id}
                            title={editingTaskId === task.id ? 'Save or Cancel to collapse' : (isExpanded ? 'Collapse' : 'Expand')}
                        >
                            {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                        </button>
                        {onTaskExplore && (
                            <button
                                className="task-compact-explore"
                                onClick={(e) => { e.stopPropagation(); onTaskExplore(task); }}
                                aria-label="View task summary"
                            >
                                <FeatherArrowRight size={14} />
                            </button>
                        )}
                    </div>
                    <div className="task-compact-line2" title={[metadataLine, task.feature_tag?.name || 'Untagged'].join(' • ')}>
                        <span className="task-compact-line2-text">{metadataLine}</span>
                        {hasBadges && (
                            <span className="task-compact-line2-badges">
                                {task.feature_tag ? (
                                    <span
                                        className="task-compact-feature-badge"
                                        style={{
                                            backgroundColor: task.feature_tag.color ? `${task.feature_tag.color}25` : '#f3f4f6',
                                            color: task.feature_tag.color || '#6b7280',
                                            borderColor: task.feature_tag.color || '#e5e7eb'
                                        }}
                                        title={task.feature_tag.name}
                                    >
                                        {task.feature_tag.name}
                                    </span>
                                ) : (
                                    <span className="task-compact-feature-badge task-compact-feature-badge--untagged" title="No feature tag">
                                        Untagged
                                    </span>
                                )}
                                {metrics.isOld && <span className="task-compact-marker-badge task-compact-marker-badge--old">30+ days old</span>}
                                {metrics.isStale && <span className="task-compact-marker-badge task-compact-marker-badge--stale">Stale</span>}
                            </span>
                        )}
                    </div>
                </div>

                {/* Expanded: dense data panel */}
                {isExpanded && (
                    <div className="task-compact-expanded" onClick={(e) => e.stopPropagation()}>
                        {editingTaskId === task.id ? (
                            <div className="task-compact-edit">
                                <div className="task-compact-edit-grid">
                                    <div className="task-compact-edit-field">
                                        <label>Title</label>
                                        <TextField>
                                            <TextField.Input
                                                value={changedTasks[task.id]?.title || task.title}
                                                onChange={(e) => handleFieldChange(task.id, 'title', e.target.value)}
                                                className="task-compact-input"
                                            />
                                        </TextField>
                                    </div>
                                    <div className="task-compact-edit-field task-compact-edit-field--full">
                                        <label>Description</label>
                                        <TextArea>
                                            <TextArea.Input
                                                value={changedTasks[task.id]?.description || task.description || ''}
                                                onChange={(e) => handleFieldChange(task.id, 'description', e.target.value)}
                                                className="task-compact-input"
                                                rows={2}
                                            />
                                        </TextArea>
                                    </div>
                                    <div className="task-compact-edit-field">
                                        <label>Task Type</label>
                                        <TaskTypeSelect
                                            value={changedTasks[task.id]?.taskType || task.task_type || DEFAULT_TASK_TYPE}
                                            onChange={(e) => handleFieldChange(task.id, 'taskType', e.target.value)}
                                            className="task-compact-select"
                                            excludeTypes={isSubtaskType(task.task_type) ? [] : [TASK_TYPE_SUBTASK]}
                                            disabled={isSubtaskType(task.task_type)}
                                        />
                                    </div>
                                    <div className="task-compact-edit-field">
                                        <label>Assigned</label>
                                        <select
                                            value={changedTasks[task.id]?.assignedTo || task.assigned_to?.id || ''}
                                            onChange={(e) => handleFieldChange(task.id, 'assignedTo', e.target.value)}
                                            className="task-compact-select"
                                        >
                                            <option value="">Unassigned</option>
                                            {filteredUsers.map(u => (
                                                <option key={u.id} value={u.id}>{u.display_name || u.email}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="task-compact-edit-field">
                                        <label>Status</label>
                                        <select
                                            value={changedTasks[task.id]?.status || task.status}
                                            onChange={(e) => handleFieldChange(task.id, 'status', e.target.value)}
                                            className="task-compact-select"
                                        >
                                            {statusOptions.map(s => (
                                                <option key={s} value={s}>{s}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="task-compact-edit-actions">
                                    <Button variant="primary" size="sm" onClick={() => handleSave(task.id)} disabled={!hasChanges(task.id) || savingTaskId === task.id}>{savingTaskId === task.id ? 'Saving...' : 'Save'}</Button>
                                    <Button variant="neutral" size="sm" onClick={() => handleCancel(task.id)}>Cancel</Button>
                                </div>
                            </div>
                        ) : (
                            <div className="task-compact-dense-grid">
                                <div className="task-compact-dense-row"><span className="task-compact-label">Description</span><span className="task-compact-value">{task.description || '—'}</span></div>
                                <div className="task-compact-dense-row task-compact-dense-row--full">
                                    <span className="task-compact-label">Feature</span>
                                    <span className="task-compact-value">
                                        <InlineTagEditor task={task} availableTags={availableTags || []} projectId={projectId} onTagUpdate={onTagUpdate} onTagCreate={onTagCreate} />
                                        {task.feature_tag && <TaskDueDate dueDate={task.feature_tag.due_date} taskStatus={task.status} />}
                                    </span>
                                </div>
                                <div className="task-compact-dense-row task-compact-dense-row--full task-compact-meta-row">
                                    <span className="task-compact-meta-item"><span className="task-compact-label">Assigned</span> <span className="task-compact-value">{task.assigned_to?.display_name || 'Unassigned'}</span></span>
                                    <span className="task-compact-meta-item"><span className="task-compact-label">Created</span> <span className="task-compact-value">{formatDate(task.created_at)} by {task.created_by?.display_name || 'Unknown'}</span></span>
                                    <span className="task-compact-meta-item"><span className="task-compact-label">Hours</span> <span className="task-compact-value">{metrics.totalHours}h</span></span>
                                    <span className="task-compact-meta-item"><span className="task-compact-label">Worked</span> <span className="task-compact-value">{metrics.workCount}x</span></span>
                                    {metrics.firstWorkedDate && <span className="task-compact-meta-item"><span className="task-compact-label">First worked</span> <span className="task-compact-value">{formatDate(metrics.firstWorkedDate)}</span></span>}
                                    {metrics.lastWorkedDate && <span className="task-compact-meta-item"><span className="task-compact-label">Last worked</span> <span className="task-compact-value">{formatDate(metrics.lastWorkedDate)}</span></span>}
                                </div>
                                {taskReleases.length > 0 && (
                                    <div className="task-compact-dense-row task-compact-dense-row--full">
                                        <span className="task-compact-label">Releases</span>
                                        <span className="task-compact-value task-compact-releases">
                                            {taskReleases.map(r => (
                                                <span
                                                    key={r.id}
                                                    className="task-release-badge task-release-badge--compact"
                                                    title={`Release: ${r.name}${r.version ? ` (v${r.version})` : ''}${r.target_date ? ` - ${formatDate(r.target_date)}` : ''}`}
                                                >
                                                    <Package size={10} />
                                                    <span className="task-release-name">{r.name}</span>
                                                    {r.version && <span className="task-release-version">v{r.version}</span>}
                                                </span>
                                            ))}
                                        </span>
                                    </div>
                                )}
                                {/* <div className="task-compact-dense-row task-compact-dense-row--full">
                                    <span className="task-compact-label">Links</span>
                                    <span className="task-compact-value">{task.linked_tasks?.length ? `${task.linked_tasks.length} linked` : '—'}</span>
                                </div> */}
                            </div>
                        )}
                        {!editingTaskId && (
                            <div className="task-compact-expanded-links">
                                <TaskLinks task={task} allTasks={allTasks} projectId={projectId} onTaskClick={(id) => { const t = allTasks.find(x => x.id === id); if (t && onTaskExplore) onTaskExplore(t); }} isManager={isManager} allowCrossProject={allowCrossProject} onAddSubtask={onAddSubtask} onTaskUpdate={setTasks ? (updatedTask) => setTasks(prev => prev.map(t => t.id === updatedTask.id ? { ...t, ...updatedTask } : t)) : undefined} onLinksUpdated={setTasks ? (taskId, links) => setTasks(prev => prev.map(t => t.id === taskId ? { ...t, linked_tasks: links } : t)) : undefined} />
                                <div className="task-compact-actions">
                                    <Button variant="neutral" size="sm" onClick={(e) => { e.stopPropagation(); startEditing(task.id); }}>Edit</Button>
                                    {userHasPrivileges && <Button variant="danger" size="sm" onClick={(e) => { e.stopPropagation(); handleDeleteClick(task); }} disabled={!canDelete}>Delete</Button>}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderTaskCardOrCompact = (task) => viewMode === 'compact' ? renderCompactTaskCard(task) : renderTaskCard(task);

    // Get subtasks for a parent (full task objects from tasks list)
    const getSubtaskBubblesForParent = (parentTask) => {
        if (!subtaskBubblesMode || viewMode !== 'compact') return null;
        const subtaskLinks = (parentTask.linked_tasks || []).filter(l => l.direction === 'subtask' && l.linked_task?.id);
        if (subtaskLinks.length === 0) return null;
        const subtasks = subtaskLinks
            .map(link => allTasks.find(t => t.id === link.linked_task.id))
            .filter(Boolean);
        return subtasks;
    };

    const renderSubtaskBubble = (task) => {
        const titleWords = (task.title || 'Untitled').split(/\s+/).slice(0, 3).join(' ');
        const tagName = task.feature_tag?.name || '—';
        const assignee = task.assigned_to?.display_name || task.assigned_to?.email || '—';
        const isExpanded = expandedCompactTasks.has(task.id);
        if (isExpanded) return null; // Rendered as full row when expanded
        return (
            <div
                key={task.id}
                className="task-subtask-bubble"
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); toggleCompactExpand(task.id, e); }}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleCompactExpand(task.id, e); } }}
                aria-label={`${task.display_id || task.id}: ${task.title}. Click to expand`}
                title={`${task.display_id || ''} ${task.title || ''} • ${assignee}`}
            >
                <span className="task-subtask-bubble-id">{task.display_id || task.id?.slice(0, 8)}</span>
                <span
                    className="task-subtask-bubble-tag"
                    style={{
                        backgroundColor: task.feature_tag?.color ? `${task.feature_tag.color}25` : '#f3f4f6',
                        color: task.feature_tag?.color || '#6b7280',
                        borderColor: task.feature_tag?.color || '#e5e7eb'
                    }}
                >
                    {tagName}
                </span>
                <span className="task-subtask-bubble-title">{titleWords}</span>
                <span className="task-subtask-bubble-assignee">{assignee}</span>
            </div>
        );
    };

    const renderTaskWithBubbles = (task) => {
        const isSubtask = subtaskIds.has(task.id);
        if (subtaskBubblesMode && viewMode === 'compact' && isSubtask) {
            const isExpanded = expandedCompactTasks.has(task.id);
            if (isExpanded) return renderTaskCardOrCompact(task); // Expanded: show full card
            return null; // Collapsed: rendered under parent
        }
        const subtasks = getSubtaskBubblesForParent(task);
        if (subtaskBubblesMode && viewMode === 'compact' && subtasks && subtasks.length > 0) {
            return (
                <React.Fragment key={task.id}>
                    {renderTaskCardOrCompact(task)}
                    <div className="task-subtask-bubbles-row">
                        {subtasks.map(st => renderSubtaskBubble(st))}
                    </div>
                </React.Fragment>
            );
        }
        return renderTaskCardOrCompact(task);
    };

    const renderTasks = () => {
        if (groupingMode === 'feature-tag' && Object.keys(groupedTasks).length > 0) {
            // Render grouped by feature tag view
            return (
                <div className="task-groups-container">
                    {Object.entries(groupedTasks).map(([groupId, group]) => (
                        <div key={groupId} className="task-group">
                            <div 
                                className="task-group-header"
                                onClick={() => onToggleGroup && onToggleGroup(groupId)}
                            >
                                <div className="task-group-title">
                                    {group.tag && (
                                        <span 
                                            className="task-group-tag-indicator"
                                            style={{ backgroundColor: group.tag.color || '#6B7280' }}
                                        />
                                    )}
                                    <span className="task-group-name">
                                        {group.tag?.name || 'Untagged'}
                                    </span>
                                    {isFeatureOverdue && isFeatureOverdue(group.tag) && 
                                     group.tasks.some(task => task.status === 'open' || task.status === 'in-progress') && (
                                        <span className="task-group-overdue-badge">Overdue</span>
                                    )}
                                    {group.tasks.length > 0 && 
                                     group.tasks.every(task => task.status === 'completed' || task.status === 'released') && (
                                        <span className="task-group-complete-marker" title="All tasks completed">
                                            <FeatherCheckCircle size={16} />
                                        </span>
                                    )}
                                    <span className="task-group-count">({group.totalCount != null ? group.totalCount : group.tasks.length})</span>
                                </div>
                                {expandedGroups.has(groupId) ? (
                                    <ChevronUp size={16} />
                                ) : (
                                    <ChevronDown size={16} />
                                )}
                            </div>
                            {expandedGroups.has(groupId) && (
                                <div className="task-group-content">
                                    <div className={`tasks-grid ${viewMode === 'list' ? 'list-view' : ''} ${viewMode === 'compact' ? 'compact-view' : ''}`}>
                                        {group.tasks.map(task => renderTaskWithBubbles(task))}
                                    </div>
                                    {group.hasMore && onLoadMoreGroup && (
                                        <div className="task-group-load-more">
                                            <Button
                                                variant="neutral-tertiary"
                                                onClick={() => onLoadMoreGroup(groupId)}
                                                disabled={loadingMoreGroupId === groupId}
                                            >
                                                {loadingMoreGroupId === groupId ? 'Loading...' : 'Load more'}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        } else if (groupingMode === 'work-plan' && Object.keys(groupedTasksByWorkPlan).length > 0) {
            // Render grouped by work plan (user) view
            return (
                <div className="task-groups-container">
                    {Object.entries(groupedTasksByWorkPlan).map(([groupId, group]) => (
                        <div key={groupId} className="task-group">
                            <div 
                                className="task-group-header"
                                onClick={() => onToggleGroup && onToggleGroup(groupId)}
                            >
                                <div className="task-group-title">
                                    <span className="task-group-user-avatar">
                                        {group.user?.display_name?.charAt(0)?.toUpperCase() || group.user?.email?.charAt(0)?.toUpperCase() || '?'}
                                    </span>
                                    <span className="task-group-name">
                                        {group.user?.display_name || group.user?.email || 'Unknown User'}
                                    </span>
                                    <span className="task-group-count">({group.totalCount != null ? group.totalCount : group.tasks.length})</span>
                                </div>
                                {expandedGroups.has(groupId) ? (
                                    <ChevronUp size={16} />
                                ) : (
                                    <ChevronDown size={16} />
                                )}
                            </div>
                            {expandedGroups.has(groupId) && (
                                <div className="task-group-content">
                                    <div className={`tasks-grid ${viewMode === 'list' ? 'list-view' : ''} ${viewMode === 'compact' ? 'compact-view' : ''}`}>
                                        {group.tasks.map(task => renderTaskWithBubbles(task))}
                                    </div>
                                    {group.hasMore && onLoadMoreGroup && (
                                        <div className="task-group-load-more">
                                            <Button variant="neutral-tertiary" onClick={() => onLoadMoreGroup(groupId)} disabled={loadingMoreGroupId === groupId}>
                                                {loadingMoreGroupId === groupId ? 'Loading...' : 'Load more'}
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            );
        } else if (groupingMode === 'releases' && Object.keys(groupedTasksByReleases).length > 0) {
            // Render grouped by releases view
            return (
                <div className="task-groups-container">
                    {Object.entries(groupedTasksByReleases).map(([groupId, group]) => {
                        const fullRelease = group.release?.id && group.release.id !== 'not-in-release' 
                            ? releases.find(r => r.id === group.release.id) 
                            : null;
                        const isReady = fullRelease && isReleaseReadyForRelease(fullRelease, allTasks);
                        
                        return (
                            <div key={groupId} className="task-group">
                                <div 
                                    className="task-group-header"
                                    onClick={() => onToggleGroup && onToggleGroup(groupId)}
                                >
                                    <div className="task-group-title">
                                        <span className="task-group-name">
                                            {group.release?.name || 'Unknown Release'}
                                            {group.release?.version && (
                                                <span className="task-group-release-version"> ({group.release.version})</span>
                                            )}
                                        </span>
                                        {isReady && <ReadyForReleaseBadge />}
                                        <span className="task-group-count">({group.totalCount != null ? group.totalCount : group.tasks.length})</span>
                                    </div>
                                    {expandedGroups.has(groupId) ? (
                                        <ChevronUp size={16} />
                                    ) : (
                                        <ChevronDown size={16} />
                                    )}
                                </div>
                                {expandedGroups.has(groupId) && (
                                    <div className="task-group-content">
                                        <div className={`tasks-grid ${viewMode === 'list' ? 'list-view' : ''} ${viewMode === 'compact' ? 'compact-view' : ''}`}>
                                            {group.tasks.map(task => renderTaskWithBubbles(task))}
                                        </div>
                                        {group.hasMore && onLoadMoreGroup && (
                                            <div className="task-group-load-more">
                                                <Button variant="neutral-tertiary" onClick={() => onLoadMoreGroup(groupId)} disabled={loadingMoreGroupId === groupId}>
                                                    {loadingMoreGroupId === groupId ? 'Loading...' : 'Load more'}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        } else if (groupingMode === 'task-links' && Object.keys(groupedTasksByLinks).length > 0) {
            // Compact tree: root row + linked children (depends_on, blocked_by, duplicates, subtask)
            const OUTGOING_DIRECTIONS = ['depends_on', 'blocked_by', 'duplicates', 'subtask'];
            const isOutgoing = (d) => OUTGOING_DIRECTIONS.includes((d || '').toLowerCase());
            const renderCompactLinkRow = (link, isRoot = false) => {
                const lt = link?.linked_task || link;
                const fullTask = allTasks.find(t => t.id === lt.id);
                const isCrossProject = projectId && lt.project?.id && lt.project.id !== projectId;
                const direction = link?.direction || link?.relationship_type;
                const assignee = (fullTask || lt).assigned_to?.display_name || (fullTask || lt).assigned_to?.email || null;
                const featureTag = (fullTask || lt).feature_tag;
                const dueDate = featureTag?.due_date;
                const handleClick = () => {
                    if (fullTask && onTaskExplore) onTaskExplore(fullTask);
                };
                const rowClass = `task-tree-row ${isRoot ? 'task-tree-row-root' : ''} ${fullTask ? 'clickable' : ''}`;
                return (
                    <div
                        key={link?.id || lt.id}
                        className={rowClass}
                        onClick={fullTask ? handleClick : undefined}
                        role={fullTask ? 'button' : undefined}
                        tabIndex={fullTask ? 0 : undefined}
                        onKeyDown={fullTask ? (e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(); } : undefined}
                    >
                        <span className="task-tree-row-id">{lt.display_id || lt.id?.slice(0, 8)}</span>
                        <span className="task-tree-row-type">{(fullTask || lt).task_type || DEFAULT_TASK_TYPE}</span>
                        <span className="task-tree-row-title">{lt.title || 'Untitled'}</span>
                        {direction && <RelationshipBadge direction={direction} className="task-tree-row-badge" />}
                        {assignee && <span className="task-tree-row-assignee" title={assignee}>{assignee}</span>}
                        {featureTag && (
                            <span className="task-tree-row-tag" style={{ backgroundColor: featureTag.color ? `${featureTag.color}20` : '#F3F4F6', color: featureTag.color || '#6B7280' }} title={featureTag.name}>
                                {featureTag.name}
                            </span>
                        )}
                        {dueDate && (
                            <span className="task-tree-row-due" title={`Due: ${new Date(dueDate).toLocaleDateString()}`}>
                                {formatDate(dueDate)}
                            </span>
                        )}
                        <span className={`task-tree-row-status ${getStatusBadgeClass(lt.status || 'open')}`}>{lt.status || 'open'}</span>
                        {isCrossProject && lt.project && (
                            <span className="task-tree-row-project" title={lt.project.name}>{lt.project.abbreviation || lt.project.name}</span>
                        )}
                    </div>
                );
            };
            return (
                <div className="task-groups-container task-links-compact">
                    {Object.entries(groupedTasksByLinks).map(([groupId, group]) => {
                        const isWithLinks = groupId === 'with-links';
                        const groupLabel = isWithLinks ? 'Tasks with links' : 'Tasks without links';
                        const isEmptyWithLinks = isWithLinks && (!group.tasks || group.tasks.length === 0);
                        return (
                            <div key={groupId} className="task-group task-group-compact">
                                <div
                                    className="task-group-header task-group-header-compact"
                                    onClick={() => onToggleGroup && onToggleGroup(groupId)}
                                >
                                    <span className="task-group-name">{groupLabel}</span>
                                    <span className="task-group-count">{group.totalCount != null ? group.totalCount : group.tasks.length}</span>
                                    {expandedGroups.has(groupId) ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </div>
                                {expandedGroups.has(groupId) && (
                                    <div className="task-group-content task-group-content-compact">
                                        {isEmptyWithLinks ? (
                                            <div className="task-tree-empty">No tasks with links</div>
                                        ) : isWithLinks ? (
                                            <div className="task-tree-list">
                                                {group.tasks
                                                    .filter(task => !subtaskIds.has(task.id))
                                                    .map(task => {
                                                    const outgoingLinks = (task.linked_tasks || []).filter(link => isOutgoing(link.direction || link.relationship_type));
                                                    return (
                                                    <div key={task.id} className="task-tree-item">
                                                        <div
                                                            className="task-tree-item-header"
                                                            onClick={() => onToggleGroup && onToggleGroup(task.id)}
                                                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggleGroup && onToggleGroup(task.id); } }}
                                                            role="button"
                                                            tabIndex={0}
                                                            title="Expand/collapse"
                                                        >
                                                            <span className="task-tree-item-toggle">
                                                                {expandedGroups.has(task.id) ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                                            </span>
                                                            <span className="task-tree-item-label">
                                                                <span
                                                                    className="task-tree-item-label-clickable"
                                                                    onClick={(e) => { e.stopPropagation(); onTaskExplore && onTaskExplore(task); }}
                                                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); onTaskExplore && onTaskExplore(task); } }}
                                                                    role="button"
                                                                    tabIndex={0}
                                                                    title="Open task details"
                                                                >
                                                                    <span className="task-tree-item-type">{task.task_type || DEFAULT_TASK_TYPE}</span>
                                                                    {task.display_id || task.id?.slice(0, 8)} {(task.title || '').slice(0, 60)}{(task.title || '').length > 60 ? '…' : ''}
                                                                </span>
                                                            </span>
                                                            <span className="task-tree-item-meta">
                                                                {task.assigned_to && (
                                                                    <span className="task-tree-item-assignee" title={task.assigned_to.display_name || task.assigned_to.email}>
                                                                        {task.assigned_to.display_name || task.assigned_to.email}
                                                                    </span>
                                                                )}
                                                                {task.feature_tag && (
                                                                    <span className="task-tree-item-tag" style={{ backgroundColor: task.feature_tag.color ? `${task.feature_tag.color}25` : '#F3F4F6', color: task.feature_tag.color || '#6B7280' }} title={task.feature_tag.name}>
                                                                        {task.feature_tag.name}
                                                                    </span>
                                                                )}
                                                                {task.feature_tag?.due_date && (
                                                                    <span className="task-tree-item-due" title={`Due: ${new Date(task.feature_tag.due_date).toLocaleDateString()}`}>
                                                                        {formatDate(task.feature_tag.due_date)}
                                                                    </span>
                                                                )}
                                                                <span className={getStatusBadgeClass(task.status)}>{task.status}</span>
                                                                <span className="task-tree-item-count">{outgoingLinks.length}</span>
                                                            </span>
                                                        </div>
                                                        {expandedGroups.has(task.id) && (
                                                            <div className="task-tree-item-body">
                                                                {outgoingLinks.map(link => renderCompactLinkRow(link))}
                                                            </div>
                                                        )}
                                                    </div>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <>
                                                <div className={`tasks-grid ${viewMode === 'list' ? 'list-view' : ''} ${viewMode === 'compact' ? 'compact-view' : ''}`}>
                                                    {group.tasks.map(task => renderTaskWithBubbles(task))}
                                                </div>
                                            </>
                                        )}
                                        {!isEmptyWithLinks && group.hasMore && onLoadMoreGroup && (
                                            <div className="task-group-load-more">
                                                <Button variant="neutral-tertiary" onClick={() => onLoadMoreGroup(groupId)} disabled={loadingMoreGroupId === groupId}>
                                                    {loadingMoreGroupId === groupId ? 'Loading...' : 'Load more'}
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            );
        } else {
            // Render ungrouped view (existing behavior)
            return (
                <>
                    <div className={`tasks-grid ${viewMode === 'list' ? 'list-view' : ''} ${viewMode === 'compact' ? 'compact-view' : ''}`}>
                        {tasks.map(task => renderTaskWithBubbles(task))}
                    </div>
                    {hasMoreFlat && onLoadMoreFlat && (
                        <div className="task-group-load-more">
                            <Button variant="neutral-tertiary" onClick={onLoadMoreFlat} disabled={loadingMoreFlat}>
                                {loadingMoreFlat ? 'Loading...' : 'Load more'}
                            </Button>
                        </div>
                    )}
                </>
            );
        }
    };

    const hasGroupsToShow = groupingMode !== 'none' && (
        Object.keys(groupedTasks).length > 0 ||
        Object.keys(groupedTasksByWorkPlan).length > 0 ||
        Object.keys(groupedTasksByReleases).length > 0 ||
        Object.keys(groupedTasksByLinks).length > 0
    );
    const hasContentToShow = hasGroupsToShow || tasks.length > 0;

    return (
        <div className="components-Tasks-TaskList">
            <div className="task-list">
                {!hasContentToShow ? (
                    <div className="no-tasks">
                        No tasks found
                    </div>
                ) : (
                    renderTasks()
                )}

            {/* Attachments Modal */}
            {showAttachments && selectedTask && (
                <div className="attachments-modal">
                    <div className="attachments-modal-content">
                        <div className="attachments-modal-header">
                            <h3>Attachments for {selectedTask.title}</h3>
                            <button onClick={handleCloseAttachments} className="close-button">&times;</button>
                        </div>
                        <div className="attachments-list">
                            {editingTaskId === selectedTask.id && (
                                <div style={{ marginBottom: '1rem' }}>
                                    <input
                                        type="file"
                                        onChange={(e) => handleAttachmentUpload(selectedTask.id, e)}
                                        disabled={uploading}
                                    />
                                    {uploadError && <div style={{ color: 'red', fontSize: '0.85rem' }}>{uploadError}</div>}
                                </div>
                            )}
                            {selectedTask.attachments.map(attachment => (
                                <div key={attachment.id} className="attachment-item" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <a 
                                        href={attachment.url} 
                                        target="_blank" 
                                        rel="noopener noreferrer"
                                        className="attachment-link"
                                    >
                                        <FaPaperclip />
                                        <span>{attachment.file_name}</span>
                                    </a>
                                    {editingTaskId === selectedTask.id && (
                                        <button
                                            className="delete-attachment-btn"
                                            title="Delete attachment"
                                            onClick={() => handleDeleteAttachment(attachment.id)}
                                            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', marginLeft: '0.5rem' }}
                                        >
                                            <FaTrash />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {deleteConfirmation && (
                <div className="delete-confirmation-modal">
                    <div className="delete-confirmation-modal-content">
                        <div className="delete-confirmation-modal-header">
                            <div className="delete-confirmation-icon">
                                <AlertTriangle size={24} />
                            </div>
                            <h3>Delete Task Confirmation</h3>
                            <button onClick={handleDeleteCancel} className="close-button">&times;</button>
                        </div>
                        <div className="delete-confirmation-body">
                            <p className="delete-warning-text">
                                This action cannot be undone. Are you sure you want to delete this task?
                            </p>
                            <div className="delete-task-info">
                                <strong>Task:</strong> {deleteConfirmation.task.title}
                                {deleteConfirmation.task.display_id && (
                                    <span className="task-display-id"> [{deleteConfirmation.task.display_id}]</span>
                                )}
                            </div>
                            
                            {(() => {
                                const { associations } = deleteConfirmation;
                                const hasAssociations = 
                                    associations.workPlans.length > 0 ||
                                    associations.featureTag ||
                                    associations.releases.length > 0 ||
                                    associations.taskAttachments > 0 ||
                                    associations.workPlanAttachments > 0;

                                if (!hasAssociations) {
                                    return null;
                                }

                                return (
                                    <div className="delete-associations-list">
                                        <strong>The following associations exist:</strong>
                                        <ul>
                                            {associations.workPlans.length > 0 && (
                                                <li>
                                                    Worked on in <strong>{associations.workPlans.length}</strong> work plan(s)
                                                    {associations.hasNotes && ' (with notes)'}
                                                </li>
                                            )}
                                            {associations.featureTag && (
                                                <li>
                                                    Part of feature: <strong>{associations.featureTag.name}</strong>
                                                </li>
                                            )}
                                            {associations.releases.length > 0 && (
                                                <li>
                                                    Feature is in release(s):{' '}
                                                    {associations.releases.map((r, idx) => (
                                                        <span key={r.id}>
                                                            <strong>{r.name}</strong>
                                                            {r.version && ` (${r.version})`}
                                                            {idx < associations.releases.length - 1 && ', '}
                                                        </span>
                                                    ))}
                                                </li>
                                            )}
                                            {associations.taskAttachments > 0 && (
                                                <li>
                                                    Has <strong>{associations.taskAttachments}</strong> attachment(s) that will be deleted
                                                </li>
                                            )}
                                            {associations.workPlanAttachments > 0 && (
                                                <li>
                                                    Has <strong>{associations.workPlanAttachments}</strong> work plan attachment(s) that will be deleted
                                                </li>
                                            )}
                                        </ul>
                                    </div>
                                );
                            })()}
                        </div>
                        <div className="delete-confirmation-actions">
                            <Button 
                                variant="neutral"
                                onClick={handleDeleteCancel}
                                disabled={deleting}
                            >
                                Cancel
                            </Button>
                            <Button 
                                variant="danger"
                                onClick={handleDeleteConfirm}
                                disabled={deleting}
                            >
                                {deleting ? 'Deleting...' : 'Delete Task'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Feature Tag Detail Modal */}
            {selectedFeatureTag && (
                <FeatureTagDetailModal
                    tag={selectedFeatureTag}
                    isOpen={isFeatureTagModalOpen}
                    onClose={() => {
                        setIsFeatureTagModalOpen(false);
                        setSelectedFeatureTag(null);
                    }}
                    tasks={tasks.filter(t => t.feature_tag?.id === selectedFeatureTag.id)}
                    users={users || []}
                />
            )}
        </div>
        </div>
    );
};

export default TaskList; 